/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo, Suspense, lazy, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/AdminSidebar';
import Header from '../components/AdminHeader';
import QuickActions from '../components/QuickActions';
import StatCard from '../components/StatCard';
import { DashboardSkeleton } from '../components/EnhancedSkeletonLoader';
import { useAdminDashboardData } from '../hooks/useAdminDashboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserTie, faFileAlt, faBriefcase, faChartLine, faCalendar, faUserGraduate, faBuilding, faGraduationCap, faEye } from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminDashboard.css';

// Lazy load heavy components - only load when needed (code splitting)
const JobSeekerMatrix = lazy(() => import('../components/JobSeekerMatrix'));
const RecruiterMatrix = lazy(() => import('../components/RecruiterMatrix'));
const InternMatrix = lazy(() => import('../components/InternMatrix'));
const EmployerMatrix = lazy(() => import('../components/EmployerMatrix'));
const PlanManagement = lazy(() => import('../components/PlanManagement'));
const AnalyticsDashboard = lazy(() => import('../components/AdminAnalyticsDashboard'));
const UserManagement = lazy(() => import('../components/UserManagement'));
const AccountApproval = lazy(() => import('../components/AccountApproval'));
const SwahiliAnalysisManagement = lazy(() => import('../components/SwahiliAnalysisManagement'));
const BulkImport = lazy(() => import('../components/BulkImport'));
const DashboardTemplateEditor = lazy(() => import('../components/DashboardTemplateEditor'));
const AdminMessaging = lazy(() => import('../components/AdminMessaging'));
const EmployerBrowser = lazy(() => import('../components/EmployerBrowser'));
const ProfileBrowser = lazy(() => import('../components/ProfileBrowser'));
const LegalDocumentsManager = lazy(() => import('../components/LegalDocumentsManager'));
const EmailServicesMonitor = lazy(() => import('../components/EmailServicesMonitor'));
const AdminTestimonialsManager = lazy(() => import('../components/AdminTestimonialsManager'));
const AdminTeamManager = lazy(() => import('../components/AdminTeamManager'));
const AdminVolunteerCardGenerator = lazy(() => import('../components/AdminVolunteerCardGenerator'));
const BlogManagement = lazy(() => import('../components/BlogManagement'));
const SystemSettings = lazy(() => import('../components/SystemSettings'));
const Marketing = lazy(() => import('../components/Marketing'));
const FAQManagement = lazy(() => import('../components/FAQManagement'));
const HelpVideosManagement = lazy(() => import('../components/HelpVideosManagement'));
const AdminControlPanel = lazy(() => import('./AdminControlPanel'));
const AdminDeactivateJobs = lazy(() => import('./AdminDeactivateJobs'));
const JobShareManager = lazy(() => import('../components/JobShareManager'));
const AdminExpoGalleryManager = lazy(() => import('../components/AdminExpoGalleryManager'));
const AdminEventsManager = lazy(() => import('../components/AdminEventsManager'));

// Lightweight loading fallback for lazy components
const ComponentLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '200px',
    padding: '20px'
  }}>
    <div style={{ 
      width: '40px', 
      height: '40px', 
      border: '4px solid #f3f4f6', 
      borderTop: '4px solid #3b82f6', 
      borderRadius: '50%', 
      animation: 'spin 1s linear infinite' 
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = localStorage.getItem('role');
  const userType = localStorage.getItem('userType');
  const email = localStorage.getItem('email') || localStorage.getItem('userEmail');
  const isSuperAdmin = (() => {
    const normalized = [role, userType]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase().replace(/[^a-z0-9]/g, ''));

    // Handles: superadmin, super_admin, super-admin, "Super Administrator", etc.
    const matchesType = normalized.some((v) => v === 'superadmin' || v === 'superadministrator' || v.startsWith('superadmin'));
    const matchesEmail = String(email || '').toLowerCase().trim() === 'superadmin@aksharjobs.com';
    return matchesType || matchesEmail;
  })();

  const defaultView = isSuperAdmin ? 'approvals' : 'dashboard';
  const viewFromUrl = searchParams.get('view') || defaultView;
  const [activeView, setActiveView] = useState(viewFromUrl);
  const navigate = useNavigate();
  
  // Get admin user info
  const adminName = localStorage.getItem('firstName') || 'Admin';
  const adminEmail = localStorage.getItem('email') || 'admin@rocketmatch.com';

  // Use React Query hook for optimized data fetching
  const { 
    systemStats, 
    isLoading,
    isCriticalDataLoading,
    refetch 
  } = useAdminDashboardData();

  // Sync activeView with URL params
  useEffect(() => {
    const urlView = searchParams.get('view') || defaultView;
    if (urlView !== activeView) {
      setActiveView(urlView);
    }
  }, [searchParams, activeView, defaultView]);

  // Memoize changeView to prevent unnecessary re-renders
  const changeView = useCallback((newView) => {
    setSearchParams({ view: newView });
    setActiveView(newView);
  }, [setSearchParams]);

  // Ensure default view is reflected in URL (so reload/share keeps the correct tab)
  useEffect(() => {
    if (!searchParams.get('view')) {
      setSearchParams({ view: defaultView }, { replace: true });
      setActiveView(defaultView);
    }
  }, [defaultView, searchParams, setSearchParams]);

  // Initial load - check auth only
  useEffect(() => {
    // Check if user is admin
    const token = localStorage.getItem('token');
    
    // Check if user is logged in and is admin
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Check if user has admin role (role could be 'admin' or userType could be 'admin')
    if (role !== 'admin' && userType !== 'admin' && role !== 'superadmin' && userType !== 'superadmin') {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Memoize stat cards to prevent unnecessary re-renders
  const statCards = useMemo(() => [
    { title: 'Visitors Today', value: systemStats.todayVisitors || 0, icon: faEye, iconColor: '#ef4444' },
    { title: 'Total Job Seekers', value: systemStats.totalJobSeekers, icon: faUsers, iconColor: '#3b82f6' },
    { title: 'Total Recruiters', value: systemStats.totalRecruiters, icon: faUserTie, iconColor: '#8b5cf6' },
    { title: 'Total Interns', value: systemStats.totalInterns, icon: faUserGraduate, iconColor: '#10b981' },
    { title: 'Total Jobs', value: systemStats.totalJobs, icon: faBriefcase, iconColor: '#f59e0b' },
    { title: 'Total Internships', value: systemStats.totalInternships, icon: faGraduationCap, iconColor: '#06b6d4' },
    { title: 'Total Employers', value: systemStats.totalEmployers, icon: faBuilding, iconColor: '#ec4899' },
    { title: 'Total Users', value: systemStats.totalUsers, icon: faUsers, iconColor: '#6366f1' },
    { title: 'Monthly Active Users', value: systemStats.monthlyActiveUsers, icon: faCalendar, iconColor: '#8b5cf6' }
  ], [systemStats]);

  // Memoize dashboard content to prevent re-renders
  const dashboardContent = useMemo(() => (
    <div className="admin-dashboard-main">
      <div className="dashboard-header-section">
        <div>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
            Welcome back, {adminName}!
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', margin: '0' }}>
            System Control Center - Manage your platform
          </p>
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
          System Statistics
        </h3>
        <button
          onClick={() => refetch()}
          disabled={isCriticalDataLoading}
          style={{
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isCriticalDataLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: isCriticalDataLoading ? 0.6 : 1
          }}
          title="Refresh statistics"
        >
          <FontAwesomeIcon icon={faChartLine} />
          {isCriticalDataLoading ? 'Refreshing...' : 'Refresh Stats'}
        </button>
      </div>
      <div className="stats-grid" style={{ marginTop: '0' }}>
        {statCards.map((stat, index) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            iconColor={stat.iconColor}
          />
        ))}
      </div>
      
      <QuickActions
        theme="admin"
        onBrowseJobs={() => changeView('jobseeker-matrix')}
        onUpdateResume={() => changeView('bulk-import')}
        onEditProfile={() => changeView('users')}
        customActions={[
          {
            id: 'analytics',
            text: 'VIEW ANALYTICS',
            emoji: '📊',
            onClick: () => changeView('analytics'),
            description: 'View system analytics and reports'
          },
          {
            id: 'plans',
            text: 'MANAGE PLANS',
            emoji: '💳',
            onClick: () => changeView('plans'),
            description: 'Manage subscription plans'
          },
          {
            id: 'settings',
            text: 'SYSTEM SETTINGS',
            emoji: '⚙️',
            onClick: () => changeView('settings'),
            description: 'Configure system settings'
          }
        ]}
      />
    </div>
  ), [adminName, systemStats, isCriticalDataLoading, refetch, changeView, statCards]);

  // Memoize renderMainContent to prevent unnecessary re-renders
  const renderMainContent = useMemo(() => {
    switch (activeView) {
      case 'dashboard':
        return dashboardContent;
      case 'jobseeker-matrix':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <JobSeekerMatrix />
          </Suspense>
        );
      case 'recruiter-matrix':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <RecruiterMatrix />
          </Suspense>
        );
      case 'intern-matrix':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <InternMatrix />
          </Suspense>
        );
      case 'employer-matrix':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <EmployerMatrix />
          </Suspense>
        );
      case 'plans':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <PlanManagement />
          </Suspense>
        );
      case 'analytics':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AnalyticsDashboard />
          </Suspense>
        );
      case 'template-editor':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <DashboardTemplateEditor />
          </Suspense>
        );
      case 'messaging':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminMessaging />
          </Suspense>
        );
      case 'users':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <UserManagement />
          </Suspense>
        );
      case 'approvals':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AccountApproval />
          </Suspense>
        );
      case 'swahili-analysis':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <SwahiliAnalysisManagement />
          </Suspense>
        );
      case 'bulk-import':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <BulkImport />
          </Suspense>
        );
      case 'employer-browser':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <EmployerBrowser />
          </Suspense>
        );
      case 'profile-browser':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <ProfileBrowser />
          </Suspense>
        );
      case 'testimonials':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminTestimonialsManager />
          </Suspense>
        );
      case 'blogs':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <BlogManagement />
          </Suspense>
        );
      case 'events':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminEventsManager />
          </Suspense>
        );
      case 'expo-gallery':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminExpoGalleryManager />
          </Suspense>
        );
      case 'team-members':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminTeamManager />
          </Suspense>
        );
      case 'volunteer-cards':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminVolunteerCardGenerator />
          </Suspense>
        );
      case 'legal':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <LegalDocumentsManager />
          </Suspense>
        );
      case 'faqs':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <FAQManagement />
          </Suspense>
        );
      case 'help-videos':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <HelpVideosManagement />
          </Suspense>
        );
      case 'email-services':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <EmailServicesMonitor />
          </Suspense>
        );
      case 'deactivate-jobs':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminDeactivateJobs />
          </Suspense>
        );
      case 'share-jobs':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <JobShareManager />
          </Suspense>
        );
      case 'settings':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <SystemSettings />
          </Suspense>
        );
      case 'marketing':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <Marketing />
          </Suspense>
        );
      case 'about-platform':
        return (
          <Suspense fallback={<ComponentLoader />}>
            <AdminControlPanel initialTab="about" />
          </Suspense>
        );
      default:
        return <div>Select a view from the sidebar</div>;
    }
  }, [activeView, dashboardContent, changeView]);

  // Show skeleton loader instead of full spinner for better perceived performance
  // Only show full spinner on initial load when no data exists
  if (isLoading && !systemStats.totalUsers && activeView === 'dashboard') {
    return (
      <div className="admin-dashboard">
        <Header />
        <Sidebar activeView={activeView} setActiveView={changeView} />
        <div className="admin-content">
          <main className="admin-main" style={{ padding: '24px' }}>
            <DashboardSkeleton />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <Header />
      <Sidebar activeView={activeView} setActiveView={changeView} />
      <div className="admin-content">
        <main className="admin-main">
          {renderMainContent}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
